import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocsVerificationSideEnum } from '@models/enums';
import { DocsVerificationFileUploadApproachEnum } from '@models/enums/docs-verification/docs-verification-file-upload-approach-enum';
import type { VerificationFileState } from '@store/slices/docs-verification/docs-verification-slice';

import { DocumentsStepEnum } from '../types';

type UseGetStepTextProps = {
    documentsFiles: VerificationFileState[];
    isSuccess: boolean;
    isError: boolean;
    documentsStep: DocumentsStepEnum;
    isTwoSideDocument: boolean;
    isCameraMode: boolean;
    fileUploadApproach: DocsVerificationFileUploadApproachEnum;
};

export const useGetStepText = ({
    isSuccess,
    isError,
    documentsStep,
    documentsFiles,
    isTwoSideDocument,
    isCameraMode,
    fileUploadApproach,
}: UseGetStepTextProps) => {
    const { t } = useTranslation();

    const cameraTitle = useMemo(() => {
        const document = documentsFiles.find((item) => item.status.success);

        if (document && isTwoSideDocument) {
            if (document.side === DocsVerificationSideEnum.BACK) {
                return t('docVerificationWidget.documents.camera.takePhotoFrontSide');
            }

            return t('docVerificationWidget.documents.camera.takePhotoBackSide');
        }

        if (isCameraMode) {
            return t('docVerificationWidget.documents.titleCameraMode');
        }

        return t('docVerificationWidget.documents.title');
    }, [t, documentsFiles, isTwoSideDocument, isCameraMode]);

    const cardMessage = useMemo(() => {
        if (isSuccess) {
            if (isTwoSideDocument) {
                if (documentsStep === DocumentsStepEnum.UPLOAD_FIRST_SIDE || !documentsFiles[1]) {
                    const firstDoc = documentsFiles[0];

                    return firstDoc?.side === DocsVerificationSideEnum.FRONT
                        ? 'docVerificationWidget.documents.frontSideUploaded'
                        : 'docVerificationWidget.documents.backSideUploaded';
                }

                if (documentsStep === DocumentsStepEnum.UPLOAD_SECOND_SIDE && documentsFiles[1]) {
                    const secondDoc = documentsFiles[1];

                    return secondDoc?.side === DocsVerificationSideEnum.FRONT
                        ? 'docVerificationWidget.documents.frontSideUploaded'
                        : 'docVerificationWidget.documents.backSideUploaded';
                }
            }

            return 'common.uploadSuccessful';
        }

        return documentsFiles.find((item) => item.status.error)?.status.message;
    }, [documentsFiles, documentsStep, isSuccess, isTwoSideDocument]);

    const continueText = useMemo(() => {
        if (isSuccess) {
            const document = documentsFiles.find((item) => item.status.success);

            if (document) {
                if (isTwoSideDocument) {
                    if (documentsFiles.filter((item) => item.status.success).length === 2) {
                        return t('common.continue');
                    }
                } else {
                    return t('common.continue');
                }
            }
        }

        if (isError && fileUploadApproach === DocsVerificationFileUploadApproachEnum.PHOTO) {
            return t('common.redoPhoto');
        }

        if (fileUploadApproach === DocsVerificationFileUploadApproachEnum.FILE) {
            return '';
        }

        return t('common.takePhoto');
    }, [documentsFiles, isSuccess, isError, t, isTwoSideDocument, fileUploadApproach]);

    return { cameraTitle, cardMessage, continueText };
};
